/* eslint-disable react-refresh/only-export-components */
// Stagioni: contesto condiviso (stagione selezionata), lettura da DB e helper.
//
// La stagione selezionata vale per tutta l'app. Di default e' quella in corso;
// la scelta viene ricordata per squadra in localStorage e, se diversa dalla
// stagione in corso, finisce nell'URL come ?season=<id> (cosi' un link a una
// stagione chiusa riapre proprio quella).
import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import i18n, { currentIntlLocale } from "./i18n";
import { supabaseV2 } from "./supabase";

export type SeasonStatus = "active" | "closed";

export type SeasonTopPlayer = { player_id: string; name: string; value: number };

export interface SeasonStats {
  sessions: number;
  trainings: number;
  match_days: number;
  past_trainings: number;
  matches: number;
  excluded_matches: number;
  played: number;
  wins: number;
  draws: number;
  losses: number;
  goals_for: number;
  goals_against: number;
  responses: number;
  present: number;
  late: number;
  absent: number;
  no_response: number;
  top_scorer: SeasonTopPlayer | null;
  top_assist: SeasonTopPlayer | null;
}

export interface Season {
  id: string;
  team_id: string;
  name: string;
  starts_on: string;
  ends_on: string | null;
  status: SeasonStatus;
  closed_at: string | null;
  closed_by: string | null;
  summary: SeasonStats | null;
  stats: SeasonStats;
}

export function useTeamSeasons(teamId: string | null | undefined) {
  return useQuery({
    queryKey: ["seasons", teamId],
    enabled: !!teamId,
    queryFn: async () => {
      const { data, error } = await supabaseV2.rpc("list_team_seasons", { _team_id: teamId });
      if (error) throw error;
      return (data || []) as Season[];
    },
    staleTime: 30_000,
  });
}

type SeasonContextValue = {
  teamId: string | null;
  seasons: Season[];
  activeSeason: Season | null;
  selectedSeason: Season | null;
  /** Stagione su cui filtrare i dati. null = nessun filtro (stagioni non ancora caricate o assenti). */
  selectedSeasonId: string | null;
  isViewingClosed: boolean;
  /** true quando le stagioni sono caricate e i filtri sono affidabili. */
  ready: boolean;
  selectSeason: (seasonId: string) => void;
};

const SeasonContext = createContext<SeasonContextValue>({
  teamId: null,
  seasons: [],
  activeSeason: null,
  selectedSeason: null,
  selectedSeasonId: null,
  isViewingClosed: false,
  ready: true,
  selectSeason: () => {},
});

export function useSeason() {
  return useContext(SeasonContext);
}

const storageKey = (teamId: string) => `eb.season.${teamId}`;

function readStored(teamId: string): string | null {
  try {
    return window.localStorage.getItem(storageKey(teamId));
  } catch {
    return null;
  }
}

function writeStored(teamId: string, value: string | null) {
  try {
    if (value) window.localStorage.setItem(storageKey(teamId), value);
    else window.localStorage.removeItem(storageKey(teamId));
  } catch {
    /* ignore */
  }
}

export function SeasonProvider({ teamId, children }: { teamId: string; children: ReactNode }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const seasonsQuery = useTeamSeasons(teamId);
  const seasons = useMemo(() => seasonsQuery.data || [], [seasonsQuery.data]);
  const activeSeason = seasons.find((s) => s.status === "active") || null;

  const paramId = searchParams.get("season");
  const storedId = readStored(teamId);
  const byId = (id: string | null) => (id ? seasons.find((s) => s.id === id) || null : null);
  const selectedSeason = byId(paramId) || byId(storedId) || activeSeason || seasons[0] || null;

  const setParam = useCallback(
    (value: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) next.set("season", value);
          else next.delete("season");
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Allinea URL e localStorage alla stagione selezionata. Serve anche dopo una
  // navigazione interna che perde il query param.
  useEffect(() => {
    if (!selectedSeason) return;
    if (storedId && !byId(storedId)) writeStored(teamId, null);
    const isActive = selectedSeason.status === "active";
    if (!isActive && paramId !== selectedSeason.id) setParam(selectedSeason.id);
    if (isActive && paramId) setParam(null);
    if (paramId && byId(paramId) && paramId !== storedId) {
      writeStored(teamId, byId(paramId)?.status === "active" ? null : paramId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSeason?.id, selectedSeason?.status, paramId, teamId]);

  const selectSeason = useCallback(
    (seasonId: string) => {
      const season = seasons.find((s) => s.id === seasonId);
      if (!season) return;
      const isActive = season.status === "active";
      writeStored(teamId, isActive ? null : season.id);
      setParam(isActive ? null : season.id);
    },
    [seasons, teamId, setParam],
  );

  const value: SeasonContextValue = {
    teamId,
    seasons,
    activeSeason,
    selectedSeason,
    selectedSeasonId: selectedSeason?.id || null,
    isViewingClosed: selectedSeason?.status === "closed",
    ready: !seasonsQuery.isLoading,
    selectSeason,
  };

  return <SeasonContext.Provider value={value}>{children}</SeasonContext.Provider>;
}

/**
 * Scope dati per le query: la stagione selezionata, una stagione esplicita o
 * tutte le stagioni. `key` va messo in coda alle queryKey.
 */
export function useSeasonScope(options?: { seasonId?: string | null; allSeasons?: boolean }) {
  const { selectedSeasonId, ready } = useSeason();
  const allSeasons = !!options?.allSeasons;
  const seasonId = allSeasons ? null : options?.seasonId !== undefined ? options.seasonId : selectedSeasonId;
  return {
    seasonId,
    allSeasons,
    key: allSeasons ? "all-seasons" : seasonId || "no-season",
    ready: allSeasons || options?.seasonId !== undefined || ready,
  };
}

// ---------------------------------------------------------------------------
// Date
// ---------------------------------------------------------------------------

/** Data locale Europe/Rome (YYYY-MM-DD) di un timestamp: stessa regola del DB. */
export function romeDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("sv-SE", { timeZone: "Europe/Rome" });
}

export function addDays(isoDate: string, days: number): string {
  const d = new Date(`${isoDate}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function todayRome(): string {
  return romeDate(new Date().toISOString());
}

/** Istante di riferimento per i calcoli relativi ("ultimi 30 giorni", decay): fine stagione se chiusa. */
export function seasonReferenceNow(season: Season | null | undefined): number {
  if (season?.status === "closed" && season.ends_on) {
    return Math.min(Date.now(), new Date(`${season.ends_on}T23:59:59`).getTime());
  }
  return Date.now();
}

export function formatSeasonDate(isoDate: string | null | undefined): string {
  if (!isoDate) return "";
  return new Date(`${isoDate}T12:00:00`).toLocaleDateString(currentIntlLocale(), {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function seasonPeriodLabel(season: Pick<Season, "starts_on" | "ends_on">): string {
  const from = formatSeasonDate(season.starts_on);
  if (!season.ends_on) return i18n.t("seasons:period.open", { from }) as string;
  return i18n.t("seasons:period.closed", { from, to: formatSeasonDate(season.ends_on) }) as string;
}

/** Nome suggerito per una stagione che parte il giorno `isoDate` (stessa regola del DB). */
export function suggestSeasonName(isoDate: string): string {
  const [y, m] = isoDate.split("-").map(Number);
  if (m >= 7) return `${y}/${String((y + 1) % 100).padStart(2, "0")}`;
  return `${y - 1}/${String(y % 100).padStart(2, "0")}`;
}

// ---------------------------------------------------------------------------
// Errori dal DB
// ---------------------------------------------------------------------------

type DbError = { message?: string; details?: string; code?: string } | null | undefined;

/** Messaggio localizzato per gli errori sollevati dai trigger/RPC stagioni, null se non riconosciuto. */
export function seasonErrorMessage(error: unknown): string | null {
  const e = error as DbError;
  const message = e?.message || "";
  switch (message) {
    case "season_closed":
      return i18n.t("seasons:errors.sessionInClosed", { name: e?.details || "" }) as string;
    case "season_name_duplicate":
      return i18n.t("seasons:errors.nameDuplicate") as string;
    case "season_name_required":
      return i18n.t("seasons:errors.nameRequired") as string;
    case "invalid_cut_date":
      return i18n.t("seasons:errors.invalidCutDate") as string;
    case "season_not_latest":
      return i18n.t("seasons:errors.notLatest") as string;
    case "season_not_active":
    case "season_not_closed":
      return i18n.t("seasons:errors.stale") as string;
    case "forbidden":
      return i18n.t("seasons:errors.forbidden") as string;
    default:
      if (e?.code === "23514" && message.includes("seasons_name_length")) {
        return i18n.t("seasons:errors.nameLength") as string;
      }
      return null;
  }
}
